import { useMemo } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  Checkbox,
  FormField,
  Heading,
  Label,
  LabeledControl,
  Paragraph,
  Popover,
  Select,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';

import {
  type GetUserPropertyDetailsListingCriteriaDetailsWebDto,
  type MultiplexBuildingSessionListingCriterionWebDto,
  RentalManagerListingCriteriaType,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useScreenSize } from '../../../hooks';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import {
  type SetListingCriteriaMutationVariables,
  useSetListingCriteriaMutation,
} from '../../../hooks/useSetListingCriteriaMutation';

import { TRIGGER_LOCATIONS, TRIGGER_OBJECT } from '../../../../srcLegacy/shared/constants/ClickstreamConstants';
import {
  LC_MIN_CREDIT_SCORE_OPTIONS,
  NOT_APPLICABLE,
} from '../../../../srcLegacy/shared/constants/ListingCriteriaConstants';
import Criterion from '../../../../srcLegacy/shared/model/ListingCriteria/Criterion';

import {
  emitCreditScoreEvent,
  emitCreditScoreNegotiableEvent,
  EVENT_IDS,
} from '../../../analytics/events/screeningCriteria';
import { useAuth } from '../../AuthProvider';

import type { ChangeEvent } from 'react';

const inputMinCreditScoreName = 'criterion';
const inputNegotiableName = 'negotiable';

type Props = {
  /** Building-session criteria during multiplex wizard steps; omit on review / non-multiplex. */
  criteria?: Array<MultiplexBuildingSessionListingCriterionWebDto>;
  creditScoreMax?: string;
};

const MinCreditScoreFormField = ({ criteria, creditScoreMax: creditScoreMaxProp }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenDesktop } = useScreenSize();
  const formModeContext = useFormMode();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const triggerLocation = listingDetails?.active ? TRIGGER_LOCATIONS.POST_ACTIVATION : TRIGGER_LOCATIONS.POSTING_PATH;
  const triggerObject =
    formModeContext.mode == 'modal' ? TRIGGER_OBJECT.REVIEW : TRIGGER_OBJECT.FINANCIAL_EXPECTATIONS_PAGE;

  const listingCriteria = useMemo<Array<GetUserPropertyDetailsListingCriteriaDetailsWebDto>>(
    () => listingDetails?.listingCriteria?.criteria || [],
    [listingDetails?.listingCriteria?.criteria],
  );

  const effectiveCriteria = criteria ?? listingCriteria;

  const creditScoreMax =
    creditScoreMaxProp ??
    effectiveCriteria.find((obj) => obj.type === RentalManagerListingCriteriaType.CREDIT_SCORE_MIN)?.max ??
    '';

  const { initCriterionValue, initialNegotiableValue, targetCriteria } = useMemo(() => {
    const foundIndex = effectiveCriteria.findIndex(
      (obj) => obj.type === RentalManagerListingCriteriaType.CREDIT_SCORE_MIN,
    );
    const targetCriteria = effectiveCriteria[foundIndex];

    return {
      initCriterionValue: targetCriteria?.criterion || NOT_APPLICABLE,
      initialNegotiableValue: targetCriteria?.negotiable || false,
      targetCriteria,
    };
  }, [effectiveCriteria]);

  const isOptionValid = (option: { value: string | number }) => {
    if (option.value === NOT_APPLICABLE) return true;
    return option.value <= creditScoreMax;
  };

  const filteredOptions = useMemo(() => {
    return creditScoreMax === '' ? LC_MIN_CREDIT_SCORE_OPTIONS : LC_MIN_CREDIT_SCORE_OPTIONS.filter(isOptionValid);
  }, [creditScoreMax]);

  const updateMutation = usePropertyEditMutation({
    mutationHook: useSetListingCriteriaMutation,
    successToastMessage: 'Minimum credit score updated',
    meta: {
      invalidates: ['users-properties-criteria-get'],
      context: { api: 'userPropertyCriteriaSet', details: { listingAlias } },
    },
  });

  const criterionField = useFieldSave({
    fieldName: inputMinCreditScoreName,
    initialValue: initCriterionValue,
    mutation: updateMutation,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          ...(targetCriteria ?? {}),
          criterion: value === '' ? NOT_APPLICABLE : String(value),
          negotiable: negotiableField.value as boolean,
          isEnabled: true,
          type: RentalManagerListingCriteriaType.CREDIT_SCORE_MIN,
        }),
      ],
    }),
  });

  const negotiableField = useFieldSave({
    fieldName: inputNegotiableName,
    initialValue: initialNegotiableValue,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          ...(targetCriteria ?? {}),
          criterion: criterionField.value === '' ? NOT_APPLICABLE : String(criterionField.value),
          negotiable: Boolean(value),
          isEnabled: true,
          type: RentalManagerListingCriteriaType.CREDIT_SCORE_MIN,
        }),
      ],
    }),
  });

  const handleCreditScoreChange = (event: ChangeEvent<HTMLSelectElement>) => {
    criterionField.onChange(event.target.value);
    if (listingDetails && user) {
      emitCreditScoreEvent({
        currentProperty: listingDetails,
        user,
        triggerLocation,
        triggerObject,
      });
    }
  };

  const handleNegotiableChange = (event: ChangeEvent<HTMLInputElement>) => {
    negotiableField.onChange(event.target.checked);
    if (listingDetails && user) {
      emitCreditScoreNegotiableEvent({
        currentProperty: listingDetails,
        user,
        triggerLocation,
        triggerObject,
      });
    }
  };

  return (
    <>
      <FormField
        data-testid="MinCreditScoreFormField"
        label={
          <Label>
            <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Text textStyle="body-sm-bold">Minimum credit score</Text>
              <Popover
                tone="info"
                placement="top"
                header={<Heading level={6}>Putting credit scores in context</Heading>}
                body={
                  <Paragraph>
                    Credit scores fluctuate, and they don&apos;t show a person&apos;s history of timely rent payments.
                    Consider additional information, like income and landlord references, for a more complete
                    understanding of an applicant&apos;s finances.
                  </Paragraph>
                }
                trigger={<Popover.Trigger css={{ marginLeft: '100' }} />}
              />
            </Box>
          </Label>
        }
        control={
          <Select
            size={isScreenDesktop ? 'lg' : 'md'}
            aria-label="Minimum credit score"
            name={inputMinCreditScoreName}
            value={String(criterionField.value ?? '')}
            onChange={handleCreditScoreChange}
            onBlur={criterionField.onBlur}
            data-eventid={EVENT_IDS.CREDIT_SCORE}
          >
            {filteredOptions.map(({ label, value }) => {
              return (
                <option key={value as string} value={value}>
                  {label}
                </option>
              );
            })}
          </Select>
        }
      />

      <LabeledControl
        label={<Label>Credit score is negotiable</Label>}
        control={
          <Checkbox
            name={inputNegotiableName}
            checked={Boolean(negotiableField.value)}
            onChange={handleNegotiableChange}
            data-testid="MinCreditScoreFormField-NegotiableCheckbox"
            data-eventid={EVENT_IDS.CREDIT_SCORE_NEGOTIABLE}
          />
        }
      />

      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <TextButton
          asChild
          textStyle="body-sm"
          css={{ marginRight: '200' }}
          icon={<IconExternalOutline />}
          iconPosition="end"
        >
          <a
            href="https://www.experian.com/blogs/ask-experian/credit-education/score-basics/what-is-a-good-credit-score/"
            target="_blank"
            rel="noopener noreferrer"
          >
            How Experian talks about credit scores
          </a>
        </TextButton>
      </Box>
    </>
  );
};

export default MinCreditScoreFormField;
